import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadCode, redeemCode as redeemCodeApi } from '@alchemiesolns/product-registration';
import {
  fetchLicenses,
  fetchUserProfile,
  StoreApiError,
  type LicenseSummary,
  type UserProfileResponse,
} from '@alchemiesolns/store-api';
import { PRODUCT_ID } from '../constants/product';
import { tryEstablishLicense } from '../loginWithFirebase';
import './AccountPage.css';

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

export function AccountPage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [license, setLicense] = useState<LicenseSummary | null | undefined>(undefined);
  const [profileError, setProfileError] = useState('');
  const [licenseError, setLicenseError] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemError, setRedeemError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // Stored redemption code (PIPH-292): the user-facing identity. Read
  // from localStorage rather than from /licensesMe because (a) it's
  // synchronous, (b) it's exactly the string the user typed, and (c)
  // the server's license doc stores it under codeId — same value.
  const [storedCode, setStoredCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadLicense = useCallback(async () => {
    setLicenseError('');
    try {
      const { licenses } = await fetchLicenses();
      setLicense(licenses[PRODUCT_ID] ?? null);
    } catch (e) {
      const msg = e instanceof StoreApiError ? e.message : 'Could not load license.';
      setLicenseError(msg);
      setLicense(null);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    setProfileError('');
    try {
      const data = await fetchUserProfile();
      setProfile(data);
    } catch (e) {
      const msg = e instanceof StoreApiError ? e.message : 'Could not load profile.';
      setProfileError(msg);
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      // Read the stored redemption code synchronously alongside the
      // server fetches. Updates if the user redeems a new code below.
      setStoredCode(loadCode());
      await Promise.all([loadProfile(), loadLicense()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadProfile, loadLicense]);

  const onRedeem = async () => {
    setRedeemError('');
    setBusy(true);
    try {
      await redeemCodeApi(redeemCode.trim());
      await tryEstablishLicense(PRODUCT_ID);
      setRedeemCode('');
      // redeemCodeApi persists the code; pull the fresh value into view.
      setStoredCode(loadCode());
      await loadLicense();
    } catch (e) {
      setRedeemError(e instanceof Error ? e.message : 'Could not apply this code.');
    } finally {
      setBusy(false);
    }
  };

  const onCopyCode = useCallback(async () => {
    if (!storedCode) return;
    try {
      await navigator.clipboard.writeText(storedCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail in some environments (no permission,
      // insecure context, etc.) — fall back to selecting the text so
      // the user can copy manually.
      const el = document.getElementById('account-redemption-code-value');
      if (el && window.getSelection) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }, [storedCode]);

  const displayName = profile?.name?.trim() || profile?.email || '—';
  const institution = profile?.institution?.trim() || '—';

  return (
    <main className="account-page-main">
      <div className="account-page-inner">
        <div className="account-page-actions">
          <button type="button" className="account-page-link-button" onClick={() => navigate('/app')}>
            ← Back to app
          </button>
        </div>

        <h1 className="account-page-title">Manage account</h1>
        <p className="account-page-lede">Your profile and subscription details for Piph Author.</p>

        {loading && <p className="account-page-muted">Loading…</p>}

        <section className="account-page-card" aria-labelledby="account-profile-heading">
          <h2 id="account-profile-heading">Profile</h2>
          {profileError ? (
            <p className="account-page-error" role="alert">
              {profileError}
            </p>
          ) : null}
          <dl className="account-page-dl">
            <div className="account-page-row">
              <dt className="account-page-dt">Name</dt>
              <dd className="account-page-dd">{displayName}</dd>
            </div>
            <div className="account-page-row">
              <dt className="account-page-dt">Institution</dt>
              <dd className="account-page-dd">{institution}</dd>
            </div>
            <div className="account-page-row">
              <dt className="account-page-dt">Email</dt>
              <dd className="account-page-dd">{profile?.email ?? '—'}</dd>
            </div>
          </dl>
        </section>

        <section className="account-page-card" aria-labelledby="account-license-heading">
          <h2 id="account-license-heading">License</h2>
          {licenseError ? (
            <p className="account-page-error" role="alert">
              {licenseError}
            </p>
          ) : null}
          {!loading && !licenseError && !license ? (
            <p className="account-page-muted">No license found for this product.</p>
          ) : null}
          {license ? (
            <dl className="account-page-dl">
              <div className="account-page-row">
                <dt className="account-page-dt">License name</dt>
                <dd className="account-page-dd">{PRODUCT_ID}</dd>
              </div>
              {storedCode ? (
                <div className="account-page-row">
                  <dt className="account-page-dt">Redemption code</dt>
                  <dd className="account-page-dd account-page-code-row">
                    <code
                      id="account-redemption-code-value"
                      className="account-page-code-value"
                    >
                      {storedCode}
                    </code>
                    <button
                      type="button"
                      className="account-page-code-copy"
                      onClick={() => void onCopyCode()}
                      aria-label="Copy redemption code"
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </dd>
                </div>
              ) : null}
              <div className="account-page-row">
                <dt className="account-page-dt">Expires</dt>
                <dd className="account-page-dd">{fmtDate(license.expiresAt)}</dd>
              </div>
              <div className="account-page-row">
                <dt className="account-page-dt">Status</dt>
                <dd className="account-page-dd">{license.status}</dd>
              </div>
            </dl>
          ) : null}
        </section>

        <section className="account-page-card" aria-labelledby="account-redeem-heading">
          <h2 id="account-redeem-heading">Redeem a code</h2>
          <p className="account-page-note">Enter a license or promo code to activate access.</p>
          {redeemError ? (
            <p className="account-page-error" role="alert">
              {redeemError}
            </p>
          ) : null}
          <div className="account-page-redeem-row">
            <input
              id="account-redeem-input"
              className="account-page-redeem-input"
              type="text"
              autoComplete="off"
              placeholder="Enter code"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              className="account-page-redeem-submit"
              disabled={busy || !redeemCode.trim()}
              onClick={() => void onRedeem()}
            >
              Redeem
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
